<template>
  <v-card class="beez-card">
    <v-toolbar dense flat>
      <div class="text-capitalize beez-bold">Task Reports</div>
      <v-spacer />
      <v-tooltip bottom>
        <v-btn
          slot="activator"
          flat
          icon
          :outline="$vuetify.dark"
          :class="showFilter ? 'secondary' : ''"
          @click="showFilter = !showFilter"
        >
          <v-icon>filter_alt</v-icon>
        </v-btn>
        <span>Show Filter</span>
      </v-tooltip>
      <v-tooltip bottom>
        <v-btn
          slot="activator"
          flat
          icon
          :outline="$vuetify.dark"
          :loading="countLoader"
          :class="showMetrics ? 'secondary' : ''"
          @click="showMetrics = !showMetrics"
        >
          <v-icon>insights</v-icon>
        </v-btn>
        <span>Show/Hide Metrics</span>
      </v-tooltip>
      <v-tooltip bottom>
        <v-btn
          slot="activator"
          :disabled="!validForm"
          :loading="taskLoader"
          flat
          icon
          @click="refresh()"
        >
          <v-icon>cached</v-icon>
        </v-btn>
        <span>Refresh</span>
      </v-tooltip>
    </v-toolbar>
    <v-card-title>
      <v-list-tile-avatar>
        <v-icon>search</v-icon>
      </v-list-tile-avatar>
      <v-list-tile-content>
        <v-list-tile-sub-title>
          <v-text-field
            color="secondary"
            label="Search"
            v-model="search"
            xs5
            @change="searchTasks"
          >
          </v-text-field>
        </v-list-tile-sub-title>
      </v-list-tile-content>
      <v-tooltip bottom>
        <v-btn slot="activator" flat icon @click="clearSearch">
          <v-icon>clear</v-icon>
        </v-btn>
        <span>Clear search</span>
      </v-tooltip>
    </v-card-title>
    <v-card-text>
      <v-alert
        outline
        :value="!showFilter"
        type="info"
        style="border-radius: 5px"
      >
        Showing tasks created between {{ computedFromDate }} and
        {{ computedToDate }}
      </v-alert>
      <v-expand-transition>
        <v-card
          flat
          :class="
            $vuetify.dark
              ? 'card-dark beez-card mb-2'
              : 'card-light beez-card mb-2'
          "
          v-show="showFilter"
        >
          <v-toolbar card dense class="beez-bold">
            Task Filter Options
          </v-toolbar>
          <v-form ref="variableForm" v-model="validForm" class="pa-2">
            <v-layout align-center justify-space-between row wrap>
              <v-flex xs12 md6 class="pa-2">
                <v-text-field
                  v-model="automationRequestNumber"
                  label="Automation Request Number"
                  :rules="arNumberRules"
                  prepend-icon="description"
                ></v-text-field>
              </v-flex>
              <v-flex xs12 md6 class="pa-2">
                <v-text-field
                  v-model="executionId"
                  label="Tulip Execution Identifier"
                  prepend-icon="fingerprint"
                  :rules="uuidRules"
                  mask="nnnnnnnn-nnnn-nnnn-nnnn-nnnnnnnnnnnn"
                  return-masked-value
                ></v-text-field>
              </v-flex>
              <v-flex xs12 md6 class="pa-2">
                <v-select
                  prepend-icon="web"
                  attach
                  :items="myHoneyForms"
                  v-model="selectedForms"
                  item-text="description"
                  item-value="formKey"
                  label="Select Forms"
                  multiple
                  chips
                  clearable
                  rounded
                  style="padding-right: 10px;"
                >
                  <template v-slot:selection="data">
                    <v-chip
                      v-if="data.item.formKey"
                      :selected="data.selected"
                      close
                      @input="removeFormKey(data.item)"
                    >
                      <v-avatar class="primary"
                        ><span class="white--text body-2">{{
                          data.item.description
                            .toString()
                            .charAt(0)
                            .toUpperCase()
                        }}</span></v-avatar
                      >
                      <strong>{{ data.item.description }}</strong>
                    </v-chip>
                  </template>
                </v-select>
              </v-flex>
              <v-flex xs12 md6 class="pa-2">
                <v-select
                  prepend-icon="rule"
                  attach
                  :disabled="completedTaskOnly"
                  :items="taskStatusList"
                  v-model="selectedTaskStatus"
                  item-text="text"
                  item-value="value"
                  label="Select Task Status"
                  multiple
                  chips
                  clearable
                  rounded
                  style="padding-right: 10px;"
                >
                  <template v-slot:selection="data">
                    <v-chip
                      :selected="data.selected"
                      close
                      @input="removeStatus(data.item)"
                    >
                      <v-avatar class="primary"
                        ><span class="white--text body-2">{{
                          data.item.text
                            .toString()
                            .charAt(0)
                            .toUpperCase()
                        }}</span></v-avatar
                      >
                      <strong>{{ data.item.text }}</strong>
                    </v-chip>
                  </template>
                </v-select>
              </v-flex>
              <v-flex xs12 md6 class="pa-2">
                <user-assignment-control
                  label="Raised By"
                  v-model="raisedBy"
                  multiple
                ></user-assignment-control>
              </v-flex>
              <v-flex xs12 md6 class="pa-2">
                <user-assignment-control
                  label="Assigned To"
                  v-model="assignedTo"
                  multiple
                ></user-assignment-control>
              </v-flex>
              <v-flex xs12 md6 lg3 class="pa-2">
                <v-menu
                  v-model="fromDateMenu"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  lazy
                  transition="scale-transition"
                  offset-y
                  full-width
                  min-width="290px"
                >
                  <template v-slot:activator="{ on }">
                    <v-text-field
                      :value="computedFromDate"
                      label="Tasks created from"
                      prepend-icon="event"
                      readonly
                      v-on="on"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="fromDate"
                    :max="toDate"
                    @input="fromDateMenu = false"
                  ></v-date-picker>
                </v-menu>
              </v-flex>
              <v-flex xs12 md6 lg3 class="pa-2">
                <v-menu
                  v-model="toDateMenu"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  lazy
                  transition="scale-transition"
                  offset-y
                  full-width
                  min-width="290px"
                >
                  <template v-slot:activator="{ on }">
                    <v-text-field
                      :value="computedToDate"
                      label="Tasks created till"
                      prepend-icon="event"
                      readonly
                      v-on="on"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="toDate"
                    :min="fromDate"
                    @input="toDateMenu = false"
                  ></v-date-picker>
                </v-menu>
              </v-flex>
              <v-flex xs12 md6 lg3 class="pa-2">
                <v-menu
                  v-model="completedFromMenu"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  lazy
                  transition="scale-transition"
                  offset-y
                  full-width
                  min-width="290px"
                >
                  <template v-slot:activator="{ on }">
                    <v-text-field
                      :value="computedCompletedFromMenu"
                      label="Tasks closed from"
                      prepend-icon="event"
                      readonly
                      v-on="on"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="completedFromDate"
                    :max="completedTillDate"
                    @input="completedFromMenu = false"
                  ></v-date-picker>
                </v-menu>
              </v-flex>
              <v-flex xs12 md6 lg3 class="pa-2">
                <v-menu
                  v-model="completedTillMenu"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  lazy
                  transition="scale-transition"
                  offset-y
                  full-width
                  min-width="290px"
                >
                  <template v-slot:activator="{ on }">
                    <v-text-field
                      :value="computedCompletedTillDate"
                      label="Tasks closed till"
                      prepend-icon="event"
                      readonly
                      v-on="on"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="completedTillDate"
                    :min="completedFromDate"
                    @input="completedTillMenu = false"
                  ></v-date-picker>
                </v-menu>
              </v-flex>
              <v-flex xs12 md6 lg3 class="pa-2">
                <v-menu
                  v-model="dueFromMenu"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  lazy
                  transition="scale-transition"
                  offset-y
                  full-width
                  min-width="290px"
                >
                  <template v-slot:activator="{ on }">
                    <v-text-field
                      :value="computedDueFromDate"
                      label="Tasks due from"
                      prepend-icon="event"
                      readonly
                      v-on="on"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="dueFromDate"
                    :max="dueTillDate"
                    @input="dueFromMenu = false"
                  ></v-date-picker>
                </v-menu>
              </v-flex>
              <v-flex xs12 md6 lg3 class="pa-2">
                <v-menu
                  v-model="dueTillMenu"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  lazy
                  transition="scale-transition"
                  offset-y
                  full-width
                  min-width="290px"
                >
                  <template v-slot:activator="{ on }">
                    <v-text-field
                      :value="computedDueTillDate"
                      label="Tasks due till"
                      prepend-icon="event"
                      readonly
                      v-on="on"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="dueTillDate"
                    :min="dueFromDate"
                    @input="dueTillMenu = false"
                  ></v-date-picker>
                </v-menu>
              </v-flex>
              <v-flex xs12 md6 lg3 class="pa-2">
                <v-menu
                  v-model="followUpFromMenu"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  lazy
                  transition="scale-transition"
                  offset-y
                  full-width
                  min-width="290px"
                >
                  <template v-slot:activator="{ on }">
                    <v-text-field
                      :value="computedFollowUpFromDate"
                      label="Tasks to be followed up from"
                      prepend-icon="event"
                      readonly
                      v-on="on"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="followUpFromDate"
                    :max="followUpTillDate"
                    @input="followUpFromMenu = false"
                  ></v-date-picker>
                </v-menu>
              </v-flex>
              <v-flex xs12 md6 lg3 class="pa-2">
                <v-menu
                  v-model="followUpTillMenu"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  lazy
                  transition="scale-transition"
                  offset-y
                  full-width
                  min-width="290px"
                >
                  <template v-slot:activator="{ on }">
                    <v-text-field
                      :value="computedFollowUpTillDate"
                      label="Tasks to be followed up till"
                      prepend-icon="event"
                      readonly
                      v-on="on"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="followUpTillDate"
                    :min="followUpFromDate"
                    @input="followUpTillMenu = false"
                  ></v-date-picker>
                </v-menu>
              </v-flex>
            </v-layout>
          </v-form>
          <v-card-actions>
            <v-btn
              flat
              :loading="taskLoader"
              @click="refresh()"
              class="text-capitalize primary"
              :disabled="!validForm"
            >
              <v-icon>filter_alt</v-icon>&nbsp; Apply Filter
            </v-btn>
            <v-btn
              flat
              @click="showFilter = !showFilter"
              class="text-capitalize"
              :disabled="!validForm"
            >
              Hide Filter
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-expand-transition>
      <v-expand-transition>
        <v-card flat class="beez-bold py-1" v-if="showMetrics">
          <v-toolbar flat dense>
            Metrics
          </v-toolbar>
          <v-card-text>
            <v-layout align-center justify-space-between row wrap>
              <v-flex xs6 sm4 md3>
                <v-chip>Open Tasks : {{ countOpenTask }}</v-chip>
              </v-flex>
              <v-flex xs6 sm4 md3>
                <v-chip>Assigned Tasks : {{ countAssignedTask }}</v-chip>
              </v-flex>
              <v-flex xs6 sm4 md3>
                <v-chip>Completed Tasks : {{ countClosedTask }}</v-chip>
              </v-flex>
              <v-flex xs6 sm4 md3>
                <v-chip>Deleted Tasks : {{ countDeletedTask }}</v-chip>
              </v-flex>
            </v-layout>
          </v-card-text>
        </v-card>
      </v-expand-transition>
      <v-card flat>
        <v-toolbar dense flat card class="beez-bold">
          Task List
          <v-spacer></v-spacer>
          <v-tooltip bottom>
            <v-btn
              slot="activator"
              :disabled="!validForm"
              :loading="taskLoader"
              flat
              icon
              @click="refresh()"
            >
              <v-icon>cached</v-icon>
            </v-btn>
            <span>Refresh</span>
          </v-tooltip>
          <v-tooltip bottom>
            <v-btn
              slot="activator"
              flat
              icon
              :loading="excelDownloadLoader"
              :outline="$vuetify.dark"
              :class="showFilter ? 'secondary' : ''"
              @click="downloadExcel"
            >
              <v-icon>file_download</v-icon>
            </v-btn>
            <span>Download as Excel</span>
          </v-tooltip>
        </v-toolbar>
        <v-data-table
          :headers="headers"
          :items="taskHistory"
          item-key="taskId"
          :loading="taskLoader"
          :total-items="pagination.totalItems"
          :pagination.sync="pagination"
          :rows-per-page-items="headerPageItems"
        >
          <v-progress-linear slot="progress" color="accent" indeterminate />
          <template slot="items" slot-scope="props">
            <tr>
              <td
                @click="props.expanded = !props.expanded"
                class="text-xs-left"
              >
                <div class="body-1 font-weight-bold">
                  {{ props.item.name || props.item.formKey }}
                </div>
                <div class="caption" v-if="props.item.description">
                  {{ props.item.description }}
                </div>
                <div class="caption">
                  {{ changeCase(props.item.taskType) }}
                </div>
              </td>
              <td
                @click="props.expanded = !props.expanded"
                class="text-xs-left"
              >
                <div class="body-1 font-weight-bold">
                  {{ props.item.createdAtLocal }}
                </div>
              </td>
              <td
                @click="props.expanded = !props.expanded"
                class="text-xs-left"
              >
                <div class="body-1 font-weight-bold" v-if="props.item.assigned">
                  {{ props.item.assignedName || props.item.assigned }}
                </div>
              </td>
              <td
                @click="props.expanded = !props.expanded"
                class="text-xs-left"
              >
                <div
                  class="body-1 font-weight-bold"
                  v-if="props.item.externalRequestedBy"
                >
                  {{ props.item.externalRequestedBy }}
                </div>
              </td>
              <td
                @click="props.expanded = !props.expanded"
                class="text-xs-left"
              >
                <div class="body-1 font-weight-bold" v-if="props.item.status">
                  {{
                    props.item.status === 'assignment'
                      ? 'Assigned'
                      : props.item.status === 'create'
                      ? 'Open'
                      : props.item.status === 'delete'
                      ? 'Deleted'
                      : props.item.status === 'complete'
                      ? 'Completed'
                      : changeCase(props.item.status)
                  }}
                </div>
              </td>
              <td
                @click="props.expanded = !props.expanded"
                class="text-xs-left"
              >
                <div class="body-1 font-weight-bold">
                  {{
                    props.item.approvalStatus
                      ? 'Approved'
                      : props.item.approvalStatus === null
                      ? ''
                      : !props.item.approvalStatus
                      ? 'Rejected'
                      : props.item.approvalStatus
                  }}
                </div>
              </td>

              <td
                @click="props.expanded = !props.expanded"
                class="text-xs-left"
              >
                <div
                  v-if="
                    props.item.taskHeader &&
                      props.item.taskHeader.defaultValue &&
                      props.item.taskHeader.defaultValue.dimensionList &&
                      props.item.taskHeader.defaultValue.dimensionList.length >
                        1
                  "
                >
                  <div
                    v-if="props.item.taskHeader.defaultValue.dimensionList[0]"
                    class="py-2"
                  >
                    <div class="body-1 font-weight-bold">
                      {{
                        props.item.taskHeader.defaultValue.dimensionList[0]
                          .caption
                      }}
                    </div>
                    <span
                      >{{
                        props.item.taskHeader.defaultValue.dimensionList[0]
                          .value
                      }}
                    </span>
                  </div>
                  <div
                    v-if="props.item.taskHeader.defaultValue.dimensionList[1]"
                    class="py-2"
                  >
                    <div class="body-1 font-weight-bold">
                      {{
                        props.item.taskHeader.defaultValue.dimensionList[1]
                          .caption
                      }}
                    </div>
                    <span
                      >{{
                        props.item.taskHeader.defaultValue.dimensionList[1]
                          .value
                      }}
                    </span>
                  </div>
                </div>
              </td>

              <td
                @click="props.expanded = !props.expanded"
                class="text-xs-left"
              >
                <div
                  v-if="
                    props.item.taskHeader &&
                      props.item.taskHeader.defaultValue &&
                      props.item.taskHeader.defaultValue.metricList &&
                      props.item.taskHeader.defaultValue.metricList.length > 1
                  "
                >
                  <div
                    v-if="props.item.taskHeader.defaultValue.metricList[0]"
                    class="py-2"
                  >
                    <div>
                      {{
                        props.item.taskHeader.defaultValue.metricList[0].caption
                      }}
                    </div>
                    <div>
                      <span style="font-size: 0.8em" class="pr-2">{{
                        props.item.taskHeader.defaultValue.metricList[0].unit
                      }}</span
                      ><span
                        style="font-size: 1.2em; font-weight: 500"
                        class="text-xs-right"
                        >{{
                          numberFormat(
                            props.item.taskHeader.defaultValue.metricList[0]
                              .value
                          )
                        }}</span
                      >
                    </div>
                  </div>
                  <div
                    v-if="props.item.taskHeader.defaultValue.metricList[1]"
                    class="py-2"
                  >
                    <div>
                      {{
                        props.item.taskHeader.defaultValue.metricList[1].caption
                      }}
                    </div>
                    <div>
                      <span style="font-size: 0.8em" class="pr-2">{{
                        props.item.taskHeader.defaultValue.metricList[1].unit
                      }}</span
                      ><span
                        style="font-size: 1.2em; font-weight: 500"
                        class="text-xs-right"
                        >{{
                          numberFormat(
                            props.item.taskHeader.defaultValue.metricList[1]
                              .value
                          )
                        }}</span
                      >
                    </div>
                  </div>
                </div>
              </td>

              <td
                @click="props.expanded = !props.expanded"
                class="text-xs-left"
              >
                <div class="body-1 font-weight-bold">
                  {{ props.item.completedAtLocal }}
                </div>
              </td>
            </tr>
          </template>
          <template slot="expand" slot-scope="props">
            <task-detail :task-id="props.item.hexId"></task-detail>
          </template>
          <template slot="no-data">
            <v-alert
              outline
              style="border-radius: 10px"
              :value="true"
              color="info"
              icon="info"
            >
              No tasks found!
            </v-alert>
          </template>
          <template slot="no-results">
            <v-alert
              outline
              style="border-radius: 10px"
              :value="true"
              color="error"
              icon="warning"
            >
              No matching results found
            </v-alert>
          </template>
        </v-data-table>
      </v-card>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import TaskDetail from '@/components/task-lists/TaskDetail'
import moment from 'moment'
import _ from 'lodash'
import UserAssignmentControl from '@/components/user-management/UserAssignmentControl'
import axios from 'axios'

export default {
  name: 'TasksHistory',
  components: { TaskDetail, UserAssignmentControl },
  props: {},
  data() {
    return {
      completedTaskOnly: false,
      excelDownloadLoader: false,
      raisedBy: [],
      assignedTo: [],
      selectedForms: [],
      countLoader: false,
      showMetrics: true,
      countOpenTask: 0,
      countAssignedTask: 0,
      countClosedTask: 0,
      countDeletedTask: 0,
      taskLoader: false,
      honeyFormsLoader: false,
      showFilter: false,
      taskItems: [],
      currentPage: 0,
      sortBy: '',
      automationRequestNumber: '',
      executionId: '',
      arNumberRules: [
        v => /^\s*$|[ARar]{2}[0-9]{4}\d+$/.test(v) || 'Invalid AR number'
      ],
      uuidRules: [
        v =>
          /^\s*$|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/.test(
            v
          ) || 'Invalid UUID'
      ],
      rowsPerPage: 0,
      descending: false,
      fromDateMenu: false,
      fromDate: moment(new Date())
        .subtract(1, 'M')
        .toISOString()
        .substr(0, 10),
      toDateMenu: false,
      toDate: moment(new Date())
        .add(1, 'd')
        .toISOString()
        .substr(0, 10),
      dueFromMenu: false,
      dueFromDate: '',
      dueTillMenu: false,
      dueTillDate: '',
      followUpFromMenu: false,
      followUpFromDate: '',
      followUpTillMenu: false,
      followUpTillDate: '',
      completedFromMenu: false,
      completedFromDate: '',
      completedTillMenu: false,
      completedTillDate: '',
      pagination: {
        sortBy: 'createdAt',
        rowsPerPage: 10,
        descending: true,
        totalItems: 0,
        page: 1
      },
      headerPageItems: [5, 10, 25],
      selectedTaskStatus: [],
      taskStatusList: [
        { value: 'assignment', text: 'Assigned' },
        { value: 'create', text: 'Open' },
        { value: 'delete', text: 'Deleted' },
        { value: 'complete', text: 'Completed' }
      ],
      search: '',
      validForm: false,
      headers: [
        { text: 'Name/Type', value: 'formkey' },
        { text: 'Created At', value: 'createdAt' },
        { text: 'Assigned To', value: 'assignee' },
        { text: 'Requested By', value: 'externalRequestedBy' },
        { text: 'Status', value: 'status' },
        { text: 'Approval Status', value: 'approvalStatus' },
        { text: 'Key Properties', value: 'taskHeader', sortable: false },
        { text: 'Key Values', value: 'taskHeader', sortable: false },
        { text: 'Completed At', value: 'completedAt' }
      ]
    }
  },
  mounted() {
    this.pagination.totalItems = this.totalTasks
    this.getAssignedHoneyForms()
    this.applyRouteProperties()
  },
  watch: {
    $route() {
      this.applyRouteProperties()
    },
    pagination: {
      handler() {
        try {
          if (
            this.pagination.page !== this.currentPage ||
            this.pagination.rowsPerPage !== this.rowsPerPage ||
            this.pagination.sortBy !== this.sortBy ||
            this.pagination.descending !== this.descending
          ) {
            this.currentPage = this.pagination.page
            this.rowsPerPage = this.pagination.rowsPerPage
            this.sortBy = this.pagination.sortBy
            this.descending = this.pagination.descending
            this.refresh()
          }
          // eslint-disable-next-line no-empty
        } catch (error) {}
      },
      deep: true
    },
    totalTasks() {
      this.pagination.totalItems = this.totalTasks
    },
    myHoneyForms() {
      this.selectedForms = this.myHoneyForms.map(i => i.formKey)
    },
    dueFromDate() {
      this.dueTillDate = moment(this.dueFromDate)
        .add(1, 'd')
        .endOf('day')
        .toISOString()
        .substr(0, 10)
    },
    followUpFromDate() {
      this.followUpTillDate = moment(this.followUpFromDate)
        .add(1, 'd')
        .endOf('day')
        .toISOString()
        .substr(0, 10)
    }
  },
  methods: {
    applyRouteProperties() {
      this.search = this.$route.query.search
      if (this.$route.query.arnumber) {
        this.automationRequestNumber = this.$route.query.arnumber
      }
      if (this.$route.query.executionId) {
        this.executionId = this.$route.query.executionId
      }
      this.completedTaskOnly = this.$route.name === 'task-history'
      if (this.completedTaskOnly) {
        this.selectedTaskStatus = []
        this.selectedTaskStatus.push('complete')
      } else {
        this.selectedTaskStatus = []
      }
      this.refresh()
    },
    removeFormKey(item) {
      let index = this.selectedForms.findIndex(i => i === item.formKey)
      if (index !== -1) this.selectedForms.splice(index, 1)
    },
    removeStatus(item) {
      let index = this.selectedTaskStatus.findIndex(i => i === item.value)
      if (index !== -1) this.selectedTaskStatus.splice(index, 1)
    },
    numberFormat(param) {
      return parseFloat(param).toLocaleString()
    },
    changeCase(str) {
      return _.startCase(_.lowerCase(str))
    },
    searchTasks() {
      this.refresh()
    },
    clearSearch() {
      this.search = ''
      this.searchTasks()
    },
    async getAssignedHoneyForms() {
      this.honeyFormsLoader = true
      await this.$store.dispatch('fetchMyHoneyForms')
      this.honeyFormsLoader = false
    },
    async downloadExcel() {
      this.excelDownloadLoader = true
      let countUrl = '/api/core/tasks/download/count?'
      let urlParams = ''
      urlParams = `page=1`
      if (this.pagination.rowsPerPage !== -1) {
        urlParams =
          urlParams +
          `&size=${this.pagination.totalItems}&sort=${this.pagination.sortBy}${
            this.pagination.descending ? ',desc' : ''
          }`
      }
      urlParams = await this.fillUrlParams(urlParams)
      countUrl = countUrl + urlParams
      let countResponse = await this.$store.dispatch(
        'userTaskHistoryDownloadCount',
        countUrl
      )
      let url = '/api/core/tasks/excel'
      if (countResponse === 'no') {
        url = url + '/direct?' + urlParams
        window.open(url, '_self')
        await this.$store.dispatch('directNotification', {
          type: 'success',
          message: 'File Downloaded',
          description:
            'The user task report file has been downloaded successfully',
          timestamp: moment().format('DD MMM YYYY hh:mm:ss a')
        })
      } else {
        url = url + '?' + urlParams
        await this.$store.dispatch('userTaskHistoryDownload', url)
        await this.$store.dispatch('directNotification', {
          type: 'success',
          message: 'Email - User task report',
          description:
            'The user task report size is quite large, so we will forward the report to your email shortly.',
          timestamp: moment().format('DD MMM YYYY hh:mm:ss a')
        })
      }
      this.excelDownloadLoader = false
    },
    async getCount() {
      this.countLoader = true
      let urlParams = ''
      urlParams = await this.fillUrlParams(urlParams)
      this.countOpenTask = await this.$store.dispatch(
        'fetchTasksCount',
        urlParams + '&status=create'
      )
      this.countAssignedTask = await this.$store.dispatch(
        'fetchTasksCount',
        urlParams + '&status=assignment'
      )
      this.countClosedTask = await this.$store.dispatch(
        'fetchTasksCount',
        urlParams + '&status=complete'
      )
      this.countDeletedTask = await this.$store.dispatch(
        'fetchTasksCount',
        urlParams + '&status=delete'
      )
      this.countLoader = false
    },
    fillUrlParams: async function(urlParams) {
      if (
        this.search !== undefined &&
        this.search !== null &&
        this.search !== ''
      ) {
        urlParams = urlParams + `&search=${this.search}`
      }
      urlParams =
        urlParams +
        `&fromDate=${moment(
          this.fromDate,
          'YYYY-MM-DD'
        ).toISOString()}&toDate=${moment(
          this.toDate,
          'YYYY-MM-DD'
        ).toISOString()}`
      if (this.dueFromDate) {
        urlParams =
          urlParams +
          `&dueFrom=${moment(this.dueFromDate, 'YYYY-MM-DD').toISOString()}`
      }
      if (this.automationRequestNumber) {
        let automationRequest = await this.$store.dispatch(
          'getAutomationRequestByNumber',
          this.automationRequestNumber
        )
        if (automationRequest && automationRequest.externalId) {
          urlParams = urlParams + `&externalId=${automationRequest.externalId}`
        }
      }
      if (this.executionId) {
        let response = await axios.get(`/api/v1/executions/${this.executionId}`)
        let execution = response.data
        if (execution && execution.externalId) {
          urlParams = urlParams + `&externalId=${execution.externalId}`
        }
      }
      if (this.dueTillDate) {
        urlParams =
          urlParams +
          `&dueTill=${moment(this.dueTillDate, 'YYYY-MM-DD').toISOString()}`
      }
      if (this.followUpFromDate) {
        urlParams =
          urlParams +
          `&followUpFrom=${moment(
            this.followUpFromDate,
            'YYYY-MM-DD'
          ).toISOString()}`
      }
      if (this.followUpTillDate) {
        urlParams =
          urlParams +
          `&followUpTill=${moment(
            this.followUpTillDate,
            'YYYY-MM-DD'
          ).toISOString()}`
      }
      if (this.completedFromDate) {
        urlParams =
          urlParams +
          `&completedFrom=${moment(
            this.completedFromDate,
            'YYYY-MM-DD'
          ).toISOString()}`
      }
      if (this.completedTillDate) {
        urlParams =
          urlParams +
          `&completedTill=${moment(
            this.completedTillDate,
            'YYYY-MM-DD'
          ).toISOString()}`
      }
      if (this.selectedForms && this.selectedForms.length > 0) {
        urlParams = urlParams + `&formKeysIn=${this.selectedForms.toString()}`
      }
      if (this.selectedTaskStatus && this.selectedTaskStatus.length > 0) {
        urlParams =
          urlParams + `&statusIn=${this.selectedTaskStatus.toString()}`
      }
      if (this.raisedBy && this.raisedBy.length > 0) {
        urlParams =
          urlParams +
          `&raisedByIn=${this.raisedBy.map(i => i.username).toString()}`
      }
      if (this.assignedTo && this.assignedTo.length > 0) {
        urlParams =
          urlParams +
          `&assignedToIn=${this.assignedTo
            .map(i => i.camundaUserName)
            .toString()}`
      }
      return urlParams
    },
    async refresh() {
      if (this.$refs.variableForm.validate()) {
        this.taskLoader = true
        let urlParams = ''
        urlParams = `page=${this.pagination.page}`
        if (this.pagination.rowsPerPage !== -1) {
          urlParams =
            urlParams +
            `&size=${this.pagination.rowsPerPage}&sort=${
              this.pagination.sortBy
            }${this.pagination.descending ? ',desc' : ''}`
        }
        urlParams = await this.fillUrlParams(urlParams)
        await this.$store.dispatch('fetchTasksHistory', urlParams)
        this.taskLoader = false
      }
      await this.getCount()
    }
  },

  computed: {
    ...mapState({
      taskHistory: state => state.tasks.taskHistory,
      totalTasks: state => state.tasks.totalTasks,
      honeyUsers: state => state.honeyUsers.honeyUsers,
      myHoneyForms: state => state.honeyForms.myHoneyForms,
      currentUser: state => state.session.user
    }),
    ...mapGetters({
      can: 'can'
    }),
    computedFromDate() {
      return this.fromDate
        ? moment(this.fromDate).format('dddd, MMMM Do YYYY')
        : ''
    },
    computedToDate() {
      return this.toDate ? moment(this.toDate).format('dddd, MMMM Do YYYY') : ''
    },
    computedDueFromDate() {
      return this.dueFromDate
        ? moment(this.dueFromDate).format('dddd, MMMM Do YYYY')
        : ''
    },
    computedDueTillDate() {
      return this.dueTillDate
        ? moment(this.dueTillDate).format('dddd, MMMM Do YYYY')
        : ''
    },
    computedFollowUpFromDate() {
      return this.followUpFromDate
        ? moment(this.followUpFromDate).format('dddd, MMMM Do YYYY')
        : ''
    },
    computedFollowUpTillDate() {
      return this.followUpTillDate
        ? moment(this.followUpTillDate).format('dddd, MMMM Do YYYY')
        : ''
    },
    computedCompletedFromMenu() {
      return this.completedFromDate
        ? moment(this.completedFromDate).format('dddd, MMMM Do YYYY')
        : ''
    },
    computedCompletedTillDate() {
      return this.completedTillDate
        ? moment(this.completedTillDate).format('dddd, MMMM Do YYYY')
        : ''
    }
  }
}
</script>

<style scoped></style>
